import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import DashboardLayout from '../components/DashboardLayout';
import ProfileEditor from '../components/ProfileEditor';
import BlocksEditorWithReorder from '../components/BlocksEditorWithReorder';
import ThemeSelector from '../components/ThemeSelector';
import VCardExport from '../components/VCardExport';

const ProfilePage = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const activeTab = searchParams.get('tab') || 'profile';

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  if (authLoading || profileLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  const tabs = [
    { id: 'profile', name: 'Profile Info', description: 'Basic profile information' },
    { id: 'blocks', name: 'Content Blocks', description: 'Add and manage content' },
    { id: 'theme', name: 'Theme', description: 'Customize appearance' },
    { id: 'export', name: 'Export', description: 'Export contact card' },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Edit Profile</h1>
          <p className="text-gray-400 mt-1">
            Customize your Web3 identity and content blocks
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-dark-700 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-400'
                    : 'border-transparent text-gray-400 hover:text-white hover:border-gray-600'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="glass-dark rounded-xl border border-dark-700 p-6">
          {activeTab === 'profile' && <ProfileEditor />}
          {activeTab === 'blocks' && <BlocksEditorWithReorder />}
          {activeTab === 'theme' && <ThemeSelector />}
          {activeTab === 'export' && <VCardExport />}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
